import { useState } from 'react';
import { X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { annullaSchema, type AnnullaFormData } from '../../schemas/workflow';
import { annullaRichiesta } from '../../api/workflow';
import type { RichiestaDetail } from '../../api/richieste';

const inputCls =
  'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

interface Props {
  richiestaId: string;
  onClose: () => void;
  onSaved: (updated: RichiestaDetail) => void;
}

export default function AnnullaRichiestaModal({ richiestaId, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AnnullaFormData>({
    resolver: zodResolver(annullaSchema),
  });

  async function onSubmit(data: AnnullaFormData) {
    setServerError(null);
    try {
      const updated = await annullaRichiesta(richiestaId, data.motivazione);
      onSaved(updated);
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { detail?: string } } })?.response;
      if (resp?.status === 409) {
        setServerError(resp.data?.detail ?? t('richieste.annulla.errore_409'));
      } else {
        setServerError(t('richieste.dettaglio.errore_salvataggio'));
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark">{t('richieste.workflow.btn_annulla')}</h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-ink-mid">
              {t('richieste.annulla.descrizione')}
            </p>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('richieste.annulla.motivazione')}</label>
              <textarea
                rows={3}
                className={inputCls}
                placeholder={t('richieste.annulla.motivazione_placeholder')}
                {...register('motivazione')}
              />
              {errors.motivazione && (
                <p className="text-xs text-accent-danger mt-1">{errors.motivazione.message}</p>
              )}
            </div>
            {serverError && (
              <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
                {serverError}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50"
            >
              {t('common.azioni.annulla')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-accent-danger rounded-lg hover:bg-[#B91C1C] transition-colors disabled:opacity-60"
            >
              {isSubmitting ? t('richieste.annulla.annullamento_in_corso') : t('richieste.workflow.btn_annulla')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
